import ArtTrackIcon from "@mui/icons-material/ArtTrack";
import MoreVert from "@mui/icons-material/MoreVert";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { useNotification } from "app/NotificationProvider";
import AnimalsLocationRoundedFilledIcon from "common/icons/AnimalsLocationIconRoundedFilled";
import { useTranslation } from "common/locales";
import { FieldAnimalGroupActionMenu } from "features/fieldDetail";
import { moveAnimalsIn } from "features/singleCoverInput/UpdateBiomassAfterMoveInDialog";
import {
  ArableField,
  ARABLE_TYPE,
  Field,
  getFieldWithAnimalsForAnimalGroupId,
  openMoveOutEventDialog,
  selectAllArableFields,
  selectAllFields,
  selectAnimalGroupById,
  useAppDispatch,
  useAppSelector,
} from "model";
import { useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import FieldIcon from "./FieldIcon";

interface FieldsListProps {
  fieldIds?: string[];
  emptyText?: string;
}

const StyledListItem = styled(ListItem)`
  background-color: white;
  padding: 16px;
  width: 100%;
  margin: 0;
  box-sizing: border-box;
`;

const StyledDivider = styled(Divider)`
  background-color: #e0e0e0;
  margin: 0;
  width: 100%;
`;

const StyledLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
  flex: 1;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    text-decoration: none;
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const StyledList = styled(List)`
  padding: 0;
  width: 100%;
  box-sizing: border-box;
`;

export const ListItemStyled = styled.div`
  width: 100%;
  box-sizing: border-box;

  & .move-in-icon {
    display: none;
  }

  @media (hover: hover) {
    &:hover .move-in-icon {
      display: block;
    }
  }
`;

export default function FieldsList({ fieldIds, emptyText }: FieldsListProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [currentFieldId, setCurrentFieldId] = useState<string | null>(null);

  // Get both regular and arable fields
  const regularFields = useAppSelector(selectAllFields);
  const arableFields = useAppSelector(selectAllArableFields);

  // Combine and filter fields based on fieldIds
  let fields = [...regularFields, ...arableFields];
  if (fieldIds) {
    fields = fields.filter(({ guid }) => fieldIds.includes(guid));
  }

  const loading = useAppSelector((state) => state.fields.loading === "pending");

  // Only show loading state on initial load
  if (loading && fields.length === 0) {
    return <LoadingList />;
  }

  // Show empty state only when we have no fields and we're not loading
  if (fields.length === 0 && !loading) {
    return <EmptyList text={emptyText} />;
  }

  return (
    <StyledList disablePadding>
      {currentFieldId && (
        <FieldAnimalGroupActionMenu
          anchor={menuAnchor}
          close={closeMenu}
          fieldId={currentFieldId}
        />
      )}
      {fields.map((field: Field | ArableField, index) => (
        <div key={`field-key-${field.guid}`}>
          <FieldListItem field={field} openMenu={openMenu} />
          {index < fields.length - 1 && <StyledDivider />}
        </div>
      ))}
    </StyledList>
  );

  function openMenu(fieldId: string, anchor: HTMLButtonElement) {
    setMenuAnchor(anchor);
    setCurrentFieldId(fieldId);
  }

  function closeMenu() {
    setMenuAnchor(null);
    setCurrentFieldId(null);
  }
}

export function EmptyList({ text }: { text?: string }) {
  const { t } = useTranslation();
  return (
    <Box m={3}>
      <Typography color="textSecondary" align="center">
        {text ?? t("fieldlist.empty")}
      </Typography>
    </Box>
  );
}

export function LoadingList() {
  return (
    <List>
      <ListItem>
        <ListItemAvatar>
          <Avatar>
            <ArtTrackIcon />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={<Skeleton variant="text" width={200} />}
          secondary={<Skeleton variant="text" width={100} />}
        />
      </ListItem>
    </List>
  );
}

function FieldListItem({ field, openMenu }: FieldListItemProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { notify } = useNotification();
  const history = useHistory();
  const location = useLocation();

  // Always call hooks at the top level
  const animalGroupSelector = useAppSelector((state) =>
    "animalGroup" in field
      ? selectAnimalGroupById(state, field.animalGroup || "")
      : null
  );

  const currentFieldWithAnimalsSelector = useAppSelector(
    "animalGroup" in field
      ? getFieldWithAnimalsForAnimalGroupId(field.animalGroup)
      : () => undefined
  );

  // Check if the field is an arable field by checking for arableType property
  const isArableField = (field: Field | ArableField): field is ArableField =>
    "arableType" in field &&
    field.arableType !== undefined &&
    Object.values(ARABLE_TYPE).includes(field.arableType);

  // A pasture field is a non-arable field that has an animal group
  const isPastureField = !isArableField(field) && field.animalGroup !== null;

  // A silage field is a non-arable field with no animal group
  const isSilageField = !isArableField(field) && field.animalGroup === null;

  const regularField = !isArableField(field) ? field : undefined;
  const arableField = isArableField(field) ? field : undefined;

  const animalGroup = regularField ? animalGroupSelector : null;
  const currentFieldWithAnimals = regularField
    ? currentFieldWithAnimalsSelector
    : undefined;

  const overgrazed = Boolean(
    regularField &&
      animalGroup?.postGrazingTarget &&
      regularField.dryMatterNow &&
      regularField.dryMatterNow < animalGroup.postGrazingTarget
  );

  const hasAnimalsNow =
    regularField &&
    regularField.animalGroup &&
    regularField.guid === currentFieldWithAnimals?.guid;

  const canMoveIn =
    isPastureField &&
    regularField &&
    regularField.animalGroup &&
    regularField.guid !== currentFieldWithAnimals?.guid;

  const [isSendingMoveRequest, setSendingMoveRequest] = useState(false);

  const renderSecondaryText = () => {
    if (regularField) {
      return regularField.invalidatedTimestamp
        ? t("fieldlist.noDryMatterNow.text")
        : `${Math.round(regularField.dryMatterNow)} ${t(
            "fieldlist.dryMatterNow.unit"
          )}`;
    } else if (arableField) {
      return arableField.arableType && arableField.arableType.toLowerCase();
    }
  };

  const handleMoveInClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setSendingMoveRequest(true);
    await onMoveIn();
    setSendingMoveRequest(false);
  };

  async function onMoveIn() {
    try {
      if (regularField && currentFieldWithAnimals) {
        dispatch(
          openMoveOutEventDialog({
            open: true,
            newFieldWithAnimals: regularField,
            currentFieldWithAnimals,
          })
        );
      } else if (regularField) {
        await moveAnimalsIn(
          regularField,
          currentFieldWithAnimals,
          new Date(),
          dispatch,
          notify,
          t
        );
      }
    } catch (e) {
      console.error("Error during move-in operation:", e);
      notify(t("An error occurred while moving animals. Please try again."));
    }
  }

  const handleFieldClick = (e: React.MouseEvent) => {
    e.preventDefault();
    history.push({
      pathname: `/field/${field.guid}`,
      state: {
        previousPath: location.pathname,
        previousSearch: location.search,
      },
    });
  };

  return (
    <ListItemStyled>
      <StyledListItem>
        <StyledLink to={`/field/${field.guid}`} onClick={handleFieldClick}>
          <ListItemAvatar>
            {regularField ? (
              <FieldIcon
                geom={field.geom}
                dryMatterNow={regularField.dryMatterNow}
              />
            ) : (
              <FieldIcon geom={field.geom} dryMatterNow={100} />
            )}
          </ListItemAvatar>
          <ListItemText
            primary={field.name}
            secondary={renderSecondaryText()}
          />
          {hasAnimalsNow ? (
            <Tooltip title={t("fieldview.animalsOnThisField.text")}>
              <Box
                sx={{
                  marginLeft: "auto",
                  marginRight:
                    !isSilageField && isPastureField
                      ? "36px"
                      : overgrazed
                      ? "8.8px"
                      : 0,
                }}
              >
                <IconButton
                  aria-haspopup="true"
                  disabled={true}
                  size="large"
                  sx={{ padding: "0px" }}
                >
                  <AnimalsLocationRoundedFilledIcon
                    htmlColor={animalGroup?.color}
                  />
                </IconButton>
              </Box>
            </Tooltip>
          ) : null}
          {/*canMoveIn ? (
            <Tooltip title={t("fieldview.moveAnimals.text")}>
              <Box ml={overgrazed ? 1 : 0} mr={overgrazed ? 1.1 : 0}>
                <IconButton
                  edge="end"
                  aria-haspopup="true"
                  onClick={handleMoveInClick}
                  className="move-in-icon"
                  disabled={isSendingMoveRequest}
                  size="large"
                >
                  <AnimalsLocationRoundedIcon
                    htmlColor={animalGroup?.color}
                    fillOpacity={0.5}
                  />
                </IconButton>
              </Box>
            </Tooltip>
          ) : null*/}
          {overgrazed ? (
            <Tooltip title={t("warning.tooltip.title")} placement="top">
              <WarningIcon color="error" />
            </Tooltip>
          ) : null}
        </StyledLink>
        <ListItemSecondaryAction>
          {!isSilageField && isPastureField && (
            <IconButton
              edge="end"
              aria-haspopup="true"
              onClick={(e) => openMenu(field.guid, e.currentTarget)}
              size="large"
            >
              <MoreVert />
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </StyledListItem>
    </ListItemStyled>
  );
}

type FieldListItemProps = {
  field: Field | ArableField;
  openMenu: (guid: string, target: any) => void;
};
